import { ValuesOf } from "@aderant/aderant-web-fw-core";
import { AllowedValues, EditableBooleanFieldDefinition, EditableNumberFieldDefinition, EditableOptionsFieldDefinition, GetAllowedValues } from "../../Fields/EditableFields/FieldDefinitions";
import { ConflictsAction } from "../../Permissions";
import { satisfies } from "../../UtilityTypes";
import { Messages } from "../Messages";
import { FirmSettingsPageDefinition, FirmSettingsSectionDefinition } from "./InternalTypes/FirmSettingsPageDefinition";

const isApproverFieldEnabled = satisfies<EditableBooleanFieldDefinition>()({
    type: "boolean",
    defaultValue: false,
    name: "isApproverFieldEnabled",
    label: Messages.ALLOW_APPROVERS_TO_BE_DESIGNATED.getMessage()
} as const);

const allowStandardUsersToPerformQuickSearch = satisfies<EditableBooleanFieldDefinition>()({
    type: "boolean",
    defaultValue: false,
    name: "allowStandardUsersToPerformQuickSearch",
    label: Messages.ALLOW_STANDARD_USERS_TO_USE_QUICK_SEARCH.getMessage()
} as const);

const enableFuzzySearchOnQuickSearches = satisfies<EditableBooleanFieldDefinition>()({
    type: "boolean",
    defaultValue: false,
    name: "enableFuzzySearchOnQuickSearches",
    label: Messages.ENABLE_FUZZY_ON_QUICK_SEARCHES.getMessage()
} as const);

export const deleteQuickSearchesAfter = satisfies<EditableNumberFieldDefinition>()({
    type: "integer",
    defaultValue: 7,
    min: 1,
    max: 28,
    name: "deleteQuickSearchesAfter",
    label: Messages.DELETE_QUICK_SEARCHES_AFTER.getMessage()
} as const);

const includeDataFromPriorSearches = satisfies<EditableBooleanFieldDefinition>()({
    type: "boolean",
    defaultValue: false,
    name: "includeDataFromPriorSearches",
    label: Messages.INCLUDE_DATA_FROM_PRIOR_SEARCHES.getMessage()
} as const);

export const applyFuzzySearchByDefaultForSearchRequests = satisfies<EditableBooleanFieldDefinition>()({
    type: "boolean",
    defaultValue: false,
    name: "applyFuzzySearchByDefaultForSearchRequests",
    label: Messages.APPLY_FUZZY_SEARCH_BY_DEFAULT_FOR_SEARCH_REQUESTS.getMessage()
} as const);

/**
 * This setting is used to enable or disable all email notifications from Conflicts to all users.
 * It is used in the backend to determine if email notifications should be sent.
 * Default value is true.  If it has never been set (null/undefined), this is treated as true.  Emails
 * are only disabled if this setting is explicitly set to false.
 */
export const enableEmailNotifications = satisfies<EditableBooleanFieldDefinition>()({
    type: "boolean",
    defaultValue: true,
    name: "enableEmailNotifications",
    label: Messages.ENABLE_EMAIL_NOTIFICATIONS.getMessage()
} as const);

/**
 * Controls whether email notifications are sent when somebody is assigned to a search request.
 * Enabled by default.
 */
export const enableEmailNotificationsWhenAssigned = satisfies<EditableBooleanFieldDefinition>()({
    type: "boolean",
    defaultValue: true,
    name: "enableEmailNotificationsWhenAssigned",
    label: Messages.ENABLE_EMAIL_NOTIFICATIONS_WHEN_ASSIGNED.getMessage()
} as const);

/**
 * Controls whether email notifications are sent to the requestor when a search request is completed.
 * Enabled by default.
 */
export const enableEmailNotificationsWhenCompleted = satisfies<EditableBooleanFieldDefinition>()({
    type: "boolean",
    defaultValue: true,
    name: "enableEmailNotificationsWhenCompleted",
    label: Messages.ENABLE_EMAIL_NOTIFICATIONS_WHEN_COMPLETED.getMessage()
} as const);

/**
 * Controls whether email notifications are sent to reviewers when hits are shared.
 * Disabled by default.
 */
export const enableEmailNotificationsWhenHitsShared = satisfies<EditableBooleanFieldDefinition>()({
    type: "boolean",
    defaultValue: false,
    name: "enableEmailNotificationsWhenHitsShared",
    label: Messages.ENABLE_EMAIL_NOTIFICATIONS_WHEN_HITS_SHARED.getMessage()
} as const);

export const AutoAssignToUserTypes = {
    None: "NONE",
    RequestedBy: "REQUESTEDBY",
    CreatedBy: "CREATEDBY",
    Approver: "APPROVER"
} as const;

export type AutoAssignToUserType = ValuesOf<typeof AutoAssignToUserTypes>;

/**
 * Checks if the given value is a valid AutoAssignToUserType.
 * allowedValues is optional so that the backend can use this function to validate the data without having to fetch the isApproverFieldEnabled value.
 * This is safe because if isApproverFieldEnabled is false, approvers cannot be set and so even if this is set to "APPROVER" it should operate as if "NONE".
 */
export function isAutoAssignToUserType(value: string, allowedValues?: AllowedValues): value is AutoAssignToUserType {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return Object.values((allowedValues as { label: string; value: string }[])?.map((value) => value.value) ?? AutoAssignToUserTypes).includes(value as AutoAssignToUserType);
}

export const getAutoAssignToUserTypesDisplayValue = (userType: AutoAssignToUserType): string => {
    switch (userType) {
        case "NONE":
            return Messages.NONE.getMessage();
        case "REQUESTEDBY":
            return Messages.REQUESTED_BY.getMessage();
        case "CREATEDBY":
            return Messages.CREATED_BY.getMessage();
        case "APPROVER":
            return Messages.APPROVER.getMessage();
    }
};

const autoAssignToUserTypeAllowedValues: AllowedValues = Object.entries(AutoAssignToUserTypes).map((v) => ({ label: getAutoAssignToUserTypesDisplayValue(v[1]), value: v[1] }));
const getFilteredAllowedValuesForAutoAssignToUserType: GetAllowedValues = (currentSettingsData: Record<string, any>) => {
    const isApproverFieldEnabledValue = currentSettingsData["firm-options"]?.general?.isApproverFieldEnabled ?? isApproverFieldEnabled.defaultValue;
    return isApproverFieldEnabledValue ? autoAssignToUserTypeAllowedValues : autoAssignToUserTypeAllowedValues.filter((v) => v.value !== AutoAssignToUserTypes.Approver);
};

export const autoAssignToUserType = satisfies<EditableOptionsFieldDefinition>()({
    type: "options",
    defaultValue: AutoAssignToUserTypes.None,
    name: "autoAssignToUserType",
    label: Messages.AUTO_ASSIGN_SEARCH_REQUESTS_TO.getMessage(),
    format: "dropdown",
    getAllowedValues: getFilteredAllowedValuesForAutoAssignToUserType,
    pickListProps: { labelProps: { width: "300px", position: "left" }, style: { width: "500px" }, ariaLabel: "AutoAssignToUserType Options Dropdown" },
    isDataValid: isAutoAssignToUserType,
    getDisplayValue: getAutoAssignToUserTypesDisplayValue
} as const);

const GeneralSection = satisfies<FirmSettingsSectionDefinition>()({
    name: "general",
    displayName: Messages.GENERAL.getMessage(),
    fields: [isApproverFieldEnabled, autoAssignToUserType],
    type: "basic"
} as const);

const QuickSearchesSection = satisfies<FirmSettingsSectionDefinition>()({
    name: "quicksearch",
    displayName: Messages.QUICK_SEARCHES.getMessage(),
    fields: [enableFuzzySearchOnQuickSearches, allowStandardUsersToPerformQuickSearch, deleteQuickSearchesAfter],
    type: "basic"
} as const);

const SearchDefaultsSection = satisfies<FirmSettingsSectionDefinition>()({
    name: "searchDefaults",
    displayName: Messages.SEARCH_DEFAULTS.getMessage(),
    fields: [includeDataFromPriorSearches],
    type: "basic"
} as const);

const SearchRequestsSection = satisfies<FirmSettingsSectionDefinition>()({
    name: "searchRequests",
    displayName: Messages.SEARCH_REQUESTS.getMessage(),
    fields: [applyFuzzySearchByDefaultForSearchRequests],
    type: "basic"
} as const);

const EmailsSection = satisfies<FirmSettingsSectionDefinition>()({
    name: "emails",
    displayName: Messages.EMAILS_FIRM_SETTINGS_SECTION.getMessage(),
    fields: [enableEmailNotifications, enableEmailNotificationsWhenAssigned, enableEmailNotificationsWhenCompleted, enableEmailNotificationsWhenHitsShared],
    type: "basic"
} as const);

export const FirmOptionsDefinition = satisfies<FirmSettingsPageDefinition>()({
    name: "firm-options",
    displayName: Messages.FIRM_OPTIONS.getMessage(),
    sections: [GeneralSection, SearchDefaultsSection, SearchRequestsSection, QuickSearchesSection, EmailsSection],
    requiredPermission: ConflictsAction.ViewAndEditFirmOptions,
    dataToLoad: ["FIRM_SETTINGS_PAGE_DATA"]
} as const);
